import { useMemo } from 'react';
import { CopyOutlined, DownloadOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Dialog, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { luuTep } from '../../../../lib/luu-tep';
import { useToast } from '../../../../lib/toast';
import { MAU_DONG_LOG } from '../../constants';
import { useLogInstance } from '../../hooks/useLichSu';
import { tachDongLog, tenTepLog } from '../../nghiep-vu/lich-su';
import type { LogRobot } from '../../types';
import { TruongThongTin } from './nhan';

/** Log chạy Robot tải lên Native ở cuối mỗi lượt, gắn theo InstanceId; máy chủ đã che token và mật khẩu. */
export function HopLogRobot({ instanceId, ten, onClose }: { instanceId: string; ten: string; onClose: () => void }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const q = useLogInstance(instanceId);
  const log = q.data ?? null;

  const chep = () => {
    if (!log) return;
    // Clipboard API chỉ có trên https hoặc localhost; thiếu thì chỉ sang nút Tải về.
    const ghi = navigator.clipboard?.writeText(log.log) ?? Promise.reject(new Error('clipboard'));
    ghi.then(
      () => toast.success(t('rpaLogRobot.daChep')),
      () => toast.warning(t('rpaLogRobot.khongChepDuoc')),
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={900}
      title={t('rpaLogRobot.tieuDe', { ten: ten || instanceId })}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {log && (
            <>
              <Button variant="outline" size="md" icon={<CopyOutlined />} onClick={chep}>
                {t('rpaLogRobot.chep')}
              </Button>
              <Button
                variant="outline"
                size="md"
                icon={<DownloadOutlined />}
                onClick={() => luuTep(new Blob([log.log], { type: 'text/plain;charset=utf-8' }), tenTepLog(log))}
              >
                {t('rpaLogRobot.taiVe')}
              </Button>
            </>
          )}
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.dong')}
          </Button>
        </div>
      }
    >
      {/* Không có InstanceId thì không hỏi máy chủ (truy vấn tắt): báo thiếu log luôn. */}
      {instanceId && q.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : q.isError ? (
        <p role="alert" className="text-sm text-warning-700">
          {dichLoi(q.error)}
        </p>
      ) : log ? (
        <ThanLog log={log} />
      ) : (
        <p role="status" className="flex items-start gap-2 rounded-lg border border-warning-500 bg-warning-50 px-4 py-3 text-sm text-warning-700">
          <WarningOutlined className="mt-0.5" aria-hidden />
          <span>
            <b>{t('rpaLogRobot.thieuTieuDe')}</b> {t(instanceId ? 'rpaLogRobot.chuaCo' : 'rpaLogRobot.khongCoInstance')}
          </span>
        </p>
      )}
    </Dialog>
  );
}

function ThanLog({ log }: { log: LogRobot }) {
  const { t, locale } = useT();
  const dong = useMemo(() => tachDongLog(log.log), [log.log]);
  return (
    <>
      <dl className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <TruongThongTin nhan={t('rpaLogRobot.workflow')}>{log.workflowName}</TruongThongTin>
        <TruongThongTin nhan={t('rpaLogRobot.may')}>{log.machineName}</TruongThongTin>
        <TruongThongTin nhan={t('rpaLogRobot.taiLenLuc')}>{ngayGioVN(log.updatedAt, locale)}</TruongThongTin>
        <TruongThongTin nhan={t('rpaLogRobot.dungLuong')}>
          {t('rpaLogRobot.dungLuongGiaTri', { dong: soVN(log.lineCount, locale), kyTu: soVN(log.sizeChars, locale) })}
        </TruongThongTin>
      </dl>
      <div
        role="log"
        aria-label={t('rpaLogRobot.vungLog')}
        tabIndex={0}
        className="max-h-[55vh] overflow-auto rounded-lg bg-subtle px-3 py-2 font-mono text-xs"
      >
        {dong.map((d, i) => (
          <div key={i} className={`whitespace-pre-wrap break-words ${MAU_DONG_LOG[d.muc]}`}>
            {d.text || ' '}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-content-muted">{t('rpaLogRobot.ghiChu')}</p>
    </>
  );
}
